import React from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {FiShoppingCart, FiDollarSign, FiTool, FiFileText, FiShield, FiTruck, FiCheckCircle, FiStar, FiUsers, FiClock, FiPhone} = FiIcons;

const ServicesPage = () => {
  const mainServices = [
    {
      icon: FiShoppingCart,
      title: 'Venta de vehículos de ocasión',
      description: 'Amplia selección de vehículos de segunda mano revisados y con garantía.',
      features: [
        'Vehículos inspeccionados técnicamente',
        'Historial completo del vehículo',
        'Garantía incluida',
        'Múltiples marcas y modelos'
      ]
    },
    {
      icon: FiDollarSign,
      title: 'Financiación personalizada',
      description: 'Soluciones de financiación adaptadas a tu situación económica.',
      features: [
        'Financiación personalizada',
        'Entrada mínima flexible',
        'Mejores condiciones del mercado',
        'Gestión ágil y profesional'
      ]
    },
    {
      icon: FiTool,
      title: 'Tasación de vehículos',
      description: 'Valoramos tu vehículo actual para facilitar el cambio.',
      features: [
        'Tasación gratuita',
        'Valoración justa de mercado',
        'Proceso rápido y transparente',
        'Aceptamos como parte de pago'
      ]
    },
    {
      icon: FiFileText,
      title: 'Gestión documental',
      description: 'Nos encargamos de todos los trámites administrativos.',
      features: [
        'Transferencia de propiedad',
        'Gestión con DGT',
        'Cambio de titularidad',
        'Documentación completa'
      ]
    }
  ];

  const additionalServices = [
    {
      icon: FiShield,
      title: 'Garantía mecánica',
      description: 'Garantía mecánica incluida para tu tranquilidad y confianza',
      highlight: '12 meses'
    },
    {
      icon: FiTruck,
      title: 'Entrega a domicilio',
      description: 'Llevamos tu vehículo hasta tu casa sin coste adicional en Cáceres',
      highlight: 'Gratuita'
    },
    {
      icon: FiUsers,
      title: 'Asesoramiento personalizado',
      description: 'Te ayudamos a elegir el vehículo que mejor se adapte a tus necesidades',
      highlight: 'Experto'
    },
    {
      icon: FiClock,
      title: 'Revisión pre-entrega',
      description: 'Todos los vehículos pasan por una revisión completa antes de la entrega',
      highlight: 'Completa'
    }
  ];

  const process = [
    {
      step: '01',
      title: 'Consulta inicial',
      description: 'Nos cuentas qué tipo de vehículo buscas y cuáles son tus necesidades'
    },
    {
      step: '02',
      title: 'Selección personalizada',
      description: 'Te mostramos los vehículos que mejor se adaptan a tus criterios'
    },
    {
      step: '03',
      title: 'Prueba y revisión',
      description: 'Prueba el vehículo y revisamos juntos todos los detalles técnicos'
    },
    {
      step: '04',
      title: 'Financiación',
      description: 'Configuramos la mejor opción de financiación para tu situación'
    },
    {
      step: '05',
      title: 'Documentación',
      description: 'Gestionamos todos los trámites legales y administrativos'
    },
    {
      step: '06',
      title: 'Entrega',
      description: 'Recibes tu vehículo listo para conducir con toda la documentación'
    }
  ];

  const guarantees = [
    'Garantía mecánica incluida (12 meses)',
    'Historial de mantenimiento verificado',
    'Sin kilómetros manipulados',
    'Revisión técnica completa',
    'Documentación legal al día',
    'Soporte post-venta'
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-[#005c91] to-[#3498db] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{opacity: 0, y: 30}} 
            animate={{opacity: 1, y: 0}} 
            transition={{duration: 0.8}}
            className="text-center"
          >
            <h1 className="text-4xl lg:text-6xl font-bold mb-6">
              Nuestros servicios
            </h1>
            <p className="text-xl lg:text-2xl text-blue-100 max-w-4xl mx-auto">
              Ofrecemos un servicio completo para que la compra de tu vehículo sea una experiencia perfecta de principio a fin.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Services */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{opacity: 0, y: 30}} 
            whileInView={{opacity: 1, y: 0}} 
            transition={{duration: 0.8}}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-[#2c3e50] mb-4">
              Servicios principales
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Todo lo que necesitas para encontrar, financiar y obtener tu próximo vehículo
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {mainServices.map((service, index) => (
              <motion.div 
                key={service.title}
                initial={{opacity: 0, y: 30}} 
                whileInView={{opacity: 1, y: 0}} 
                transition={{duration: 0.6, delay: index * 0.2}}
                className="bg-gray-50 rounded-xl p-8 hover:shadow-lg transition-shadow"
              >
                <div className="w-16 h-16 bg-[#005c91] rounded-full flex items-center justify-center mb-6">
                  <SafeIcon icon={service.icon} className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-[#2c3e50] mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-6">{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center space-x-2">
                      <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-[#27ae60]" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{opacity: 0, y: 30}} 
            whileInView={{opacity: 1, y: 0}} 
            transition={{duration: 0.8}}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-[#2c3e50] mb-4">
              Servicios adicionales
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Servicios complementarios que hacen la diferencia en tu experiencia
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {additionalServices.map((service, index) => (
              <motion.div 
                key={service.title}
                initial={{opacity: 0, y: 30}} 
                whileInView={{opacity: 1, y: 0}} 
                transition={{duration: 0.6, delay: index * 0.1}}
                className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow text-center"
              >
                <div className="w-16 h-16 bg-[#e67e22] rounded-full flex items-center justify-center mx-auto mb-6">
                  <SafeIcon icon={service.icon} className="w-8 h-8 text-white" />
                </div>
                <div className="bg-[#005c91] text-white px-3 py-1 rounded-full text-sm font-semibold mb-4 inline-block">
                  {service.highlight}
                </div>
                <h3 className="text-xl font-bold text-[#2c3e50] mb-4">{service.title}</h3>
                <p className="text-gray-600">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{opacity: 0, y: 30}} 
            whileInView={{opacity: 1, y: 0}} 
            transition={{duration: 0.8}}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-[#2c3e50] mb-4">
              Nuestro proceso de venta
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Un proceso transparente y profesional que te acompaña en cada paso
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {process.map((step, index) => (
              <motion.div 
                key={step.step}
                initial={{opacity: 0, y: 30}} 
                whileInView={{opacity: 1, y: 0}} 
                transition={{duration: 0.6, delay: index * 0.1}}
                className="relative"
              >
                <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
                  <div className="text-6xl font-bold text-[#005c91] opacity-20 absolute top-4 right-4">
                    {step.step}
                  </div>
                  <div className="relative z-10">
                    <h3 className="text-xl font-bold text-[#2c3e50] mb-4">{step.title}</h3>
                    <p className="text-gray-600">{step.description}</p>
                  </div>
                </div>
                {index < process.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-0.5 bg-[#005c91] transform -translate-y-1/2 z-20"></div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Guarantees Section */}
      <section className="py-20 bg-[#005c91] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{opacity: 0, y: 30}} 
            whileInView={{opacity: 1, y: 0}} 
            transition={{duration: 0.8}}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-4">
              Nuestras garantías
            </h2>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Comprometidos con la calidad y tu tranquilidad
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {guarantees.map((guarantee, index) => (
              <motion.div 
                key={guarantee}
                initial={{opacity: 0, x: -30}} 
                whileInView={{opacity: 1, x: 0}} 
                transition={{duration: 0.6, delay: index * 0.1}}
                className="flex items-center space-x-4 bg-white bg-opacity-10 p-6 rounded-lg"
              >
                <SafeIcon icon={FiCheckCircle} className="w-8 h-8 text-[#27ae60] flex-shrink-0" />
                <span className="text-lg font-medium">{guarantee}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Financing Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div 
              initial={{opacity: 0, x: -50}} 
              whileInView={{opacity: 1, x: 0}} 
              transition={{duration: 0.8}}
            >
              <h2 className="text-4xl font-bold text-[#2c3e50] mb-6">
                Financiación a medida
              </h2>
              <div className="space-y-4 text-gray-600 text-lg leading-relaxed mb-8">
                <p>
                  Trabajamos con las mejores entidades financieras para ofrecerte las condiciones más competitivas del mercado.
                </p>
                <p>
                  Nuestro equipo te asesorará para encontrar la opción de financiación que mejor se adapte a tu situación económica y necesidades.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-6 mb-8">
                <div className="text-center p-6 bg-white rounded-lg shadow-md">
                  <div className="text-3xl font-bold text-[#005c91] mb-2">Flexible</div>
                  <div className="text-sm text-gray-600">Entrada mínima</div>
                </div>
                <div className="text-center p-6 bg-white rounded-lg shadow-md">
                  <div className="text-3xl font-bold text-[#005c91] mb-2">Personalizada</div>
                  <div className="text-sm text-gray-600">Según necesidades</div>
                </div>
                <div className="text-center p-6 bg-white rounded-lg shadow-md">
                  <div className="text-3xl font-bold text-[#005c91] mb-2">Rápida</div>
                  <div className="text-sm text-gray-600">Gestión ágil</div>
                </div>
                <div className="text-center p-6 bg-white rounded-lg shadow-md">
                  <div className="text-3xl font-bold text-[#005c91] mb-2">Competitiva</div>
                  <div className="text-sm text-gray-600">Mejores condiciones</div>
                </div>
              </div>
              <a 
                href="/contacto" 
                className="bg-[#e67e22] hover:bg-orange-600 text-white px-8 py-4 rounded-full font-semibold text-lg transition-colors inline-flex items-center space-x-2"
              >
                <SafeIcon icon={FiDollarSign} className="w-5 h-5" />
                <span>Consultar financiación</span>
              </a>
            </motion.div>
            <motion.div 
              initial={{opacity: 0, x: 50}} 
              whileInView={{opacity: 1, x: 0}} 
              transition={{duration: 0.8}}
              className="relative"
            >
              <img 
                src="https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=600&h=400&fit=crop" 
                alt="Financiación AUTOSFERSAN" 
                className="rounded-lg shadow-2xl" 
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#2c3e50] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div 
            initial={{opacity: 0, y: 30}} 
            whileInView={{opacity: 1, y: 0}} 
            transition={{duration: 0.8}}
          >
            <h2 className="text-4xl font-bold mb-4">
              ¿Preparado para encontrar tu próximo coche?
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Nuestro equipo de expertos está aquí para ayudarte en cada paso del proceso. Contáctanos y descubre la diferencia AUTOSFERSAN.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="/catalogo" 
                className="bg-[#005c91] hover:bg-[#3498db] text-white px-8 py-4 rounded-full font-semibold text-lg transition-colors"
              >
                Ver vehículos disponibles
              </a>
              <a 
                href="tel:610701517" 
                className="border-2 border-white text-white hover:bg-white hover:text-[#2c3e50] px-8 py-4 rounded-full font-semibold text-lg transition-colors inline-flex items-center space-x-2"
              >
                <SafeIcon icon={FiPhone} className="w-5 h-5" />
                <span>Llamar: 610 70 15 17</span>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;